import { BehaviorSubject, Observable } from 'rxjs';
import { NotificationDevice } from '../Models/Devices/NotificationDevice';
import { NotificationDevicesHelper } from '../Helpers/NotificationDevicesHelper';
import { authService, webSocketService } from '../App';
import { WebSocketMessageTypeIncoming } from '../Enums/WebSocketMessageTypeIncoming';
import { WsDevicesStatus } from '../Models/WebSocket/Incoming/WsDevicesStatus';
import { MessageWebSocket } from '../Models/WebSocket/MessageWebSocket';
import { MessageSock } from '../Models/WebSocket/MessageSock';
import { WsEspStatus } from '../Models/WebSocket/Incoming/WsEspStatus';
import { WsLogosStatus } from '../Models/WebSocket/Incoming/WsLogosStatus';

export class DevicesService {
  private WifiCheckReady: boolean = false;

  public FetchingReady: BehaviorSubject<boolean>;
  public RefreshNeeded: BehaviorSubject<boolean>;

  public notificationDevices!: Array<NotificationDevice>;

  private timeLevelConnectionSec = 5;

  constructor() {
    this.FetchingReady = new BehaviorSubject<boolean>(false);
    this.RefreshNeeded = new BehaviorSubject<boolean>(false);
    this.FetchData();

    let timerId = setInterval(() => this.CheckWifiStatus(), 1000);

    this.ConnectToWebsocket();
  }

  private async FetchData(forceUpdate: boolean = false) {
    this.notificationDevices = await NotificationDevicesHelper.GetDevices();
    this.FetchingReady.next(true);
  }

  public GetFetchStatus(): Observable<boolean> {
    return this.FetchingReady.asObservable();
  }

  public GetRefreshStatus(): Observable<boolean> {
    return this.RefreshNeeded.asObservable();
  }

  public SetWifiStatus() {
    this.WifiCheckReady = true;
  }

  public GetWifiStatus() {
    return this.WifiCheckReady;
  }

  public GetNotificationDevices(forceUpdate: boolean = false) {
    return this.notificationDevices;
  }

  private CheckWifiStatus() {
    if (this.notificationDevices == undefined || this.notificationDevices.length == 0) {
      return;
    }

    var changeMade = false;

    this.notificationDevices.forEach((device) => {
      var now = new Date().getTime() / 1000;
      var lastOnline = new Date(device.lastOnline).getTime() / 1000;

      if (
        lastOnline + this.timeLevelConnectionSec <= now ||
        lastOnline === undefined ||
        isNaN(lastOnline)
      ) {
        if (device.online) {
          changeMade = true;
        }
        device.online = false;
      } else {
        if (!device.online) {
          changeMade = true;
        }
        device.online = true;
      }
    });

    if (changeMade) {
      this.RefreshNeeded.next(true);
    }
  }

  private ConnectToWebsocket = () => {
    webSocketService.messagesChannel.subscribe((msg) => {
      const prodItem = Object.assign(new MessageWebSocket(), msg);
      this.HandleWebsocketMessages(prodItem, msg);
    });
  };

  private HandleWebsocketMessages = (
    prodItem: MessageWebSocket,
    msg: MessageSock,
  ) => {
    if (prodItem.Type == WebSocketMessageTypeIncoming.WsEspStatus) {
      var espState = Object.assign(new WsEspStatus(), msg);
      var foundedEsp = this.notificationDevices?.find(
        (x) => x.moduleId == espState.ModuleId,
      );

      if (foundedEsp != undefined) {
        var item = foundedEsp;
        item.ipAddress = espState.CurrentIpAddress;
        item.online = true;

        var newDevices = [...this.UpdateNotificationDevice(item)];

        this.notificationDevices = newDevices;
      }
    }

    if (prodItem.Type == WebSocketMessageTypeIncoming.WsLogosStatus) {
      var logosState = Object.assign(new WsLogosStatus(), msg);

      logosState.LogosStatus.forEach((item) => {
        var foundedLogo = this.notificationDevices.find(
          (x) => x.moduleId == item.ModuleId,
        );
        if (foundedLogo != undefined) {
          var logoItem = foundedLogo;
          logoItem.ipAddress = item.CurrentIpAddress;
          logoItem.online = true;
          this.UpdateNotificationDevice(logoItem);
        }
      });
    }

    if (prodItem.Type == WebSocketMessageTypeIncoming.WsDevicesStatus) {
      var devicesState = Object.assign(new WsDevicesStatus(), msg);

      devicesState.DevicesData.forEach((item) => {
        if (this.notificationDevices == undefined || this.notificationDevices.length == 0) {
          return;
        }

        var foundedDevice = this.notificationDevices.find(
          (x) => x.moduleId == item.ModuleId,
        );

        if (foundedDevice != undefined) {
          var deviceItem = foundedDevice;
          deviceItem.lastOnline = item.LastOnline;
        }
      });
    }
  };

  public async UpdateAndSafeNotificationDevice(newItem: NotificationDevice) {
    var index = this.notificationDevices.findIndex((x) => x.id == newItem.id);
    if (newItem.id !== undefined) {
      NotificationDevicesHelper.UpdateDevice(newItem);
      newItem.lastOnline = this.notificationDevices[index].lastOnline;
      this.notificationDevices[index] = newItem;
      return newItem;
    } else {
      var newDevice = await NotificationDevicesHelper.AddDevice(newItem);
      this.notificationDevices.push(newDevice);
      return newDevice;
    }
  }

  // update device
  public UpdateNotificationDevice(item: NotificationDevice) {
    var device = this.notificationDevices.find(
      (x) => x.moduleId == item.moduleId,
    );
    device = item;
    return this.notificationDevices;
  }
  // add device
  public async AddNotificationDevice(item: NotificationDevice) {
    this.notificationDevices.push(item);
  }

  // remove device
  public RemoveNotificationDevice(item: NotificationDevice) {
    var index = this.notificationDevices.findIndex((x) => x.id == item.id);
    if (index >= 0) {
      this.notificationDevices.splice(index, 1);
    }

    if (item.id != '') {
      NotificationDevicesHelper.DeleteDevice(item);
    }
  }

  public ReplaceNotificationDevices(items: NotificationDevice[]) {
    this.notificationDevices = items;
    NotificationDevicesHelper.ReplaceDevices(items);
    return this.notificationDevices;
  }

  public async ExportDevices() {
    let token = authService.GetToken();
    await NotificationDevicesHelper.ExportDevices(
      await this.GetNotificationDevices(),
      token,
    );
  }
}
